// client/src/utils/apiClient.js
// REAL-TIME UPDATES: Modified to dispatch events after successful API operations
// This enables automatic dashboard refresh when data changes

import axios from 'axios';
import { API_REQUEST_TIMEOUT_MS } from '../constants/time';

// ============= PRODUCTION API CLIENT SETUP =============
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_REQUEST_TIMEOUT_MS,
  withCredentials: true // Enable cookie support
});



// ============= TOKEN REFRESH MANAGEMENT =============
let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  refreshQueue = [];
};

// ============= REQUEST INTERCEPTOR =============
apiClient.interceptors.request.use(
  (config) => {

    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// ============= RESPONSE INTERCEPTOR - CENTRALIZED FIX =============
apiClient.interceptors.response.use(
  (response) => {

    
    if (response.data && typeof response.data === 'object' && response.data.status) {
      
      const innerData = response.data.data || {};
      
      response.data = {
        ...response.data,       
        ...innerData,             
        data: innerData           
      };
      
      response.message = response.data.message;
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Retry on 502/503/504 or network errors (server down during deploy)
    const isRetryable = !error.response || [502, 503, 504].includes(error.response?.status);
    if (isRetryable && (!originalRequest._retryCount || originalRequest._retryCount < 3)) {
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
      const delay = Math.pow(2, originalRequest._retryCount - 1) * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
      return apiClient(originalRequest);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh token for these endpoints
      const noRefreshEndpoints = ['/auth/login', '/auth/refresh-token', '/auth/me', '/auth/verify-2fa'];
      const shouldSkipRefresh = noRefreshEndpoints.some(endpoint => 
        originalRequest.url?.includes(endpoint)
      );
      
      if (shouldSkipRefresh) {
        return Promise.reject(error);
      }
      
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {

        await apiClient.post('/auth/refresh-token');
        
        processQueue(null, true);
        isRefreshing = false;

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ============= ENHANCED ERROR CLASS =============
export class ApiError extends Error {
  constructor(message, status, data, endpoint) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusCode = status; // alias for compatibility with useFormSubmission
    this.data = data;
    this.endpoint = endpoint;
    this.timestamp = new Date().toISOString();
  }
}

// ============= ENHANCED API CALL WRAPPER =============
const apiCall = async (requestFn, endpoint = 'unknown') => {
  try {
    return await requestFn();
  } catch (error) {
    const status = error.response?.status;
    let message = error.response?.data?.message || error.message;
    const data = error.response?.data;
    
    // Handle validation errors array
    if (data?.errors && Array.isArray(data.errors)) {
      message = data.errors.map(err => err.msg || err.message).join(', ');
    }
    
    throw new ApiError(message, status, data, endpoint);
  }
};

// ============= COMPLETE API IMPLEMENTATION =============
export const api = {
  // ============= AUTH ROUTES =============
  auth: {
    login: async (credentials) => {
      const response = await apiCall(
        () => apiClient.post('/auth/login', credentials),
        '/auth/login'
      );
      return response;
    },
    register: (userData) => apiCall(
      () => apiClient.post('/auth/register', userData),
      '/auth/register'
    ),
    logout: () => apiCall(
      () => apiClient.post('/auth/logout'),
      '/auth/logout'
    ),
    refreshToken: () => apiCall(
      () => apiClient.post('/auth/refresh-token'),
      '/auth/refresh-token'
    ),
    me: () => apiCall(
      () => apiClient.get('/auth/me'),
      '/auth/me'
    ),
    myPermissions: () => apiCall(
      () => apiClient.get('/auth/me/permissions'),
      '/auth/me/permissions'
    ),
    myRoles: () => apiCall(
      () => apiClient.get('/auth/me/roles'),
      '/auth/me/roles'
    ),
    health: () => apiCall(
      () => apiClient.get('/auth/health'),
      '/auth/health'
    ),
    unlockAccount: (data) => apiCall(
      () => apiClient.post('/auth/unlock-account', data),
      '/auth/unlock-account'
    ),
    changePassword: (data) => apiCall(
      () => apiClient.post('/auth/change-password', data),
      '/auth/change-password'
    ),
    adminResetPassword: (data) => apiCall(
      () => apiClient.post('/auth/admin-reset-password', data),
      '/auth/admin-reset-password'
    ),
    // 2FA endpoints
    setup2FA: () => apiCall(
      () => apiClient.post('/auth/setup-2fa'),
      '/auth/setup-2fa'
    ),
    verifySetup2FA: (data) => apiCall(
      () => apiClient.post('/auth/verify-setup-2fa', data),
      '/auth/verify-setup-2fa'
    ),
    verify2FA: (data) => apiCall(
      () => apiClient.post('/auth/verify-2fa', data),
      '/auth/verify-2fa'
    ),
    disable2FA: (data) => apiCall(
      () => apiClient.post('/auth/disable-2fa', data),
      '/auth/disable-2fa'
    ),
    adminReset2FA: (data) => apiCall(
      () => apiClient.post('/auth/admin-reset-2fa', data),
      '/auth/admin-reset-2fa'
    ),
    // Debug endpoints
    clearPermissionCache: (userId = null) => apiCall(
      () => apiClient.post('/auth/clear-permission-cache', { userId }),
      '/auth/clear-permission-cache'
    )
  },

  // ============= OPTIMIZED DASHBOARD API =============
  dashboard: {
    getMachines: (params = {}) => apiCall(
      () => apiClient.get('/dashboard/machines', { params }),
      '/dashboard/machines'
    )
  },

  // ============= MACHINE MANAGEMENT =============
  machines: {
    getAll: (params = {}) => apiCall(
      () => apiClient.get('/machines', { params }),
      '/machines'
    ),
    create: async (data) => {
      const result = await apiCall(
        () => apiClient.post('/machines', data),
        '/machines'
      );
      // Trigger dashboard refresh after machine creation
      window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
        detail: { reason: 'machineCreated' }
      }));
      return result;
    },
    update: async (machineId, data) => {
      const result = await apiCall(
        () => apiClient.put(`/machines/${machineId}`, data),
        `/machines/${machineId}`
      );
      // Trigger dashboard refresh after machine update
      window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
        detail: { reason: 'machineUpdated', machineId }
      }));
      return result;
    },
    delete: async (machineId) => {
      const result = await apiCall(
        () => apiClient.delete(`/machines/${machineId}`),
        `/machines/${machineId}`
      );
      // Trigger dashboard refresh after machine deletion
      window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
        detail: { reason: 'machineDeleted' }
      }));
      return result;
    },
    
    // Issues Management
    getIssues: (machineIds) => apiCall(
      () => apiClient.get('/machines/issues', { 
        params: { machineIds: Array.isArray(machineIds) ? machineIds.join(',') : machineIds }
      }),
      '/machines/issues'
    ),
    getIssuesByMachine: (machineId) => apiCall(
      () => apiClient.get(`/machines/${machineId}/issues`),
      `/machines/${machineId}/issues`
    ),
    createIssue: async (machineId, data) => {
      const result = await apiCall(
        () => apiClient.post(`/machines/${machineId}/issues`, data),
        `/machines/${machineId}/issues`
      );
      // Trigger dashboard refresh after issue creation
      window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
        detail: { reason: 'issueCreated', machineId }
      }));
      return result;
    },
    updateIssue: async (machineId, issueId, data) => {
      const result = await apiCall(
        () => apiClient.put(`/machines/${machineId}/issues/${issueId}/edit`, data),
        `/machines/${machineId}/issues/${issueId}/edit`
      );
      // Trigger dashboard refresh after issue update
      window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
        detail: { reason: 'issueUpdated', machineId, issueId }
      }));
      return result;
    },
    updateIssueStatus: async (machineId, issueId, data) => {
      const result = await apiCall(
        () => apiClient.put(`/machines/${machineId}/issues/${issueId}`, data),
        `/machines/${machineId}/issues/${issueId}`
      );
      // Trigger dashboard refresh after issue status update
      window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
        detail: { reason: 'issueStatusUpdated', machineId, issueId, status: data.status }
      }));
      return result;
    },
    resolveIssueWithAttachments: async (machineId, issueId, formData) => {
      const result = await apiCall(
        () => apiClient.put(`/machines/${machineId}/issues/${issueId}/resolve-with-attachments`, formData),
        `/machines/${machineId}/issues/${issueId}/resolve-with-attachments`
      );
      // Trigger dashboard refresh after issue resolution
      window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
        detail: { reason: 'issueResolved', machineId, issueId, status: 'resolved' }
      }));
      return result;
    },
    deleteIssue: async (machineId, issueId) => {
      const result = await apiCall(
        () => apiClient.delete(`/machines/${machineId}/issues/${issueId}`),
        `/machines/${machineId}/issues/${issueId}`
      );
      // Trigger dashboard refresh after issue deletion
      window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
        detail: { reason: 'issueDeleted', machineId, issueId }
      }));
      return result;
    },
    getPriority: (machineId) => apiCall(
      () => apiClient.get(`/machines/priority/${machineId}`),
      `/machines/priority/${machineId}`
    ),

    getWorkDistribution: () => apiCall(
      () => apiClient.get('/machines/work-distribution'),
      '/machines/work-distribution'
    ),
    getPausedIssues: () => apiCall(
      () => apiClient.get('/machines/paused-issues'),
      '/machines/paused-issues'
    ),
        getActiveHelpRequests: () => apiCall(
      () => apiClient.get('/machines/help-requests/active'),
      '/machines/help-requests/active'
    ),

    // Get parameter tree for a machine (supports lazy loading)
    getParameterTree: (machineId, includeValues = true) => apiCall(
      () => apiClient.get(`/machines/${machineId}/parameters`, {
        params: { includeValues }
      }),
      `/machines/${machineId}/parameters`
    ),
    
    // Get parameters for a specific group (for lazy loading)
    getParametersByGroup: (machineId, groupId) => apiCall(
      () => apiClient.get(`/machines/${machineId}/parameters/group/${groupId}`),
      `/machines/${machineId}/parameters/group/${groupId}`
    ),
    
    // Get summary parameters for quick view
    getParameterSummary: (machineId) => apiCall(
      () => apiClient.get(`/machines/${machineId}/parameters/summary`),
      `/machines/${machineId}/parameters/summary`
    ),
    
    // Update parameter values
    updateParameterValues: (machineId, data) => apiCall(
      () => apiClient.put(`/machines/${machineId}/parameters/values`, data),
      `/machines/${machineId}/parameters/values`
    ),
    
    // Create parameter group
    createParameterGroup: (machineId, data) => apiCall(
      () => apiClient.post(`/machines/${machineId}/parameters/groups`, data),
      `/machines/${machineId}/parameters/groups`
    ),
    
    // Update parameter group
    updateParameterGroup: (machineId, groupId, data) => apiCall(
      () => apiClient.put(`/machines/${machineId}/parameters/groups/${groupId}`, data),
      `/machines/${machineId}/parameters/groups/${groupId}`
    ),
    
    // Delete parameter group
    deleteParameterGroup: (machineId, groupId) => apiCall(
      () => apiClient.delete(`/machines/${machineId}/parameters/groups/${groupId}`),
      `/machines/${machineId}/parameters/groups/${groupId}`
    ),
    
    // Create parameter
    createParameter: (machineId, data) => apiCall(
      () => apiClient.post(`/machines/${machineId}/parameters`, data),
      `/machines/${machineId}/parameters`
    ),
    
    // Update parameter
    updateParameter: (machineId, parameterId, data) => apiCall(
      () => apiClient.put(`/machines/${machineId}/parameters/${parameterId}`, data),
      `/machines/${machineId}/parameters/${parameterId}`
    ),
    
    // Delete parameter
    deleteParameter: (machineId, parameterId) => apiCall(
      () => apiClient.delete(`/machines/${machineId}/parameters/${parameterId}`),
      `/machines/${machineId}/parameters/${parameterId}`
    ),
    
    // Get parameter history
    getParameterHistory: (machineId, parameterId, params = {}) => apiCall(
      () => apiClient.get(`/machines/${machineId}/parameters/${parameterId}/history`, { params }),
      `/machines/${machineId}/parameters/${parameterId}/history`
    ),
    
    // Get recent parameter changes
    getRecentParameterChanges: (machineId, limit = 20) => apiCall(
      () => apiClient.get(`/machines/${machineId}/parameters/history/recent`, { 
        params: { limit } 
      }),
      `/machines/${machineId}/parameters/history/recent`
    ),
    
    // Copy parameters from another machine
    copyParameters: (machineId, data) => apiCall(
      () => apiClient.post(`/machines/${machineId}/parameters/copy`, data),
      `/machines/${machineId}/parameters/copy`
    ),
    
    // Get parameter trends
    getParameterTrends: (machineId, parameterId, params = {}) => apiCall(
      () => apiClient.get(`/machines/${machineId}/parameters/${parameterId}/trends`, { params }),
      `/machines/${machineId}/parameters/${parameterId}/trends`
    ),
    
    // Get parameter branch (lazy loading)
    getParameterBranch: (machineId, groupId) => apiCall(
      () => apiClient.get(`/machines/${machineId}/parameters/groups/${groupId}/branch`),
      `/machines/${machineId}/parameters/groups/${groupId}/branch`
    ),

    // ============= SETUP SHEETS (MACHINE-MOLD PAIR PARAMETERS) =============

    // Get all setup sheets for a machine
    getSetupSheets: (machineId) => apiCall(
      () => apiClient.get(`/machines/${machineId}/setup-sheets`),
      `/machines/${machineId}/setup-sheets`
    ),

    // Get setup sheet by ID
    getSetupSheet: (sheetId) => apiCall(
      () => apiClient.get(`/setup-sheets/${sheetId}`),
      `/setup-sheets/${sheetId}`
    ),

    // Update setup sheet parameter values
    updateSetupSheetValues: (sheetId, data) => apiCall(
      () => apiClient.put(`/setup-sheets/${sheetId}/values`, data),
      `/setup-sheets/${sheetId}/values`
    ),

    // Update parameter values across ALL currently installed molds on a machine
    updateAllMoldValues: (machineId, data) => apiCall(
      () => apiClient.put(`/machines/${machineId}/setup-sheet-values`, data),
      `/machines/${machineId}/setup-sheet-values`
    ),

    // Get setup sheet history
    getSetupSheetHistory: (sheetId, params = {}) => apiCall(
      () => apiClient.get(`/setup-sheets/${sheetId}/history`, { params }),
      `/setup-sheets/${sheetId}/history`
    ),

    // Copy values from one setup sheet to another
    copySetupSheetValues: (sheetId, data) => apiCall(
      () => apiClient.post(`/setup-sheets/${sheetId}/copy-values`, data),
      `/setup-sheets/${sheetId}/copy-values`
    ),

    // ============= MACHINE PARAMETER SUBSCRIPTIONS =============

    // Get parameter subscriptions for a machine
    getParameterSubscriptions: (machineId) => apiCall(
      () => apiClient.get(`/machines/${machineId}/parameter-subscriptions`),
      `/machines/${machineId}/parameter-subscriptions`
    ),

    // Subscribe machine to parameters (bulk)
    subscribeParameters: (machineId, parameterDefinitionIds) => apiCall(
      () => apiClient.post(`/machines/${machineId}/parameter-subscriptions`, { parameterDefinitionIds }),
      `/machines/${machineId}/parameter-subscriptions`
    ),

    // Unsubscribe machine from a parameter
    unsubscribeParameter: (machineId, defId) => apiCall(
      () => apiClient.delete(`/machines/${machineId}/parameter-subscriptions/${defId}`),
      `/machines/${machineId}/parameter-subscriptions/${defId}`
    ),

    // Bulk unsubscribe machine from parameters
    unsubscribeParameters: (machineId, parameterDefinitionIds) => apiCall(
      () => apiClient.post(`/machines/${machineId}/parameter-subscriptions/bulk-unsubscribe`, { parameterDefinitionIds }),
      `/machines/${machineId}/parameter-subscriptions/bulk-unsubscribe`
    ),

    // Copy parameter subscriptions from another machine
    copyParameterSubscriptions: (machineId, sourceMachineId) => apiCall(
      () => apiClient.post(`/machines/${machineId}/parameter-subscriptions/copy`, { sourceMachineId }),
      `/machines/${machineId}/parameter-subscriptions/copy`
    ),

    // ============= LONG-TERM ACTION (DEFERRED ISSUES) =============
    
    // Defer an issue to long-term action
    deferIssue: async (machineId, issueId, data) => {
      const result = await apiCall(
        () => apiClient.post(`/machines/${machineId}/issues/${issueId}/defer`, data),
        `/machines/${machineId}/issues/${issueId}/defer`
      );
      window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
        detail: { reason: 'issueDeferred', machineId, issueId }
      }));
      return result;
    },
    
    // Resume work on a deferred issue
    resumeIssue: async (machineId, issueId) => {
      const result = await apiCall(
        () => apiClient.post(`/machines/${machineId}/issues/${issueId}/resume`),
        `/machines/${machineId}/issues/${issueId}/resume`
      );
      window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
        detail: { reason: 'issueResumed', machineId, issueId }
      }));
      return result;
    },
    
    // Reopen a deferred issue back to open status
    reopenDeferredIssue: async (machineId, issueId) => {
      const result = await apiCall(
        () => apiClient.post(`/machines/${machineId}/issues/${issueId}/reopen-deferred`),
        `/machines/${machineId}/issues/${issueId}/reopen-deferred`
      );
      window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
        detail: { reason: 'issueReopened', machineId, issueId }
      }));
      return result;
    },
    
    // Get all deferred issues (across all machines)
    getDeferredIssues: (params = {}) => apiCall(
      () => apiClient.get('/machines/deferred-issues', { params }),
      '/machines/deferred-issues'
    ),
    
    // Get deferred issues for a specific machine
    getDeferredIssuesByMachine: (machineId, params = {}) => apiCall(
      () => apiClient.get(`/machines/${machineId}/deferred-issues`, { params }),
      `/machines/${machineId}/deferred-issues`
    ),

    // ============= WORK NOTES =============

    getWorkNotes: (machineId, issueId) => apiCall(
      () => apiClient.get(`/machines/${machineId}/issues/${issueId}/work-notes`),
      `/machines/${machineId}/issues/${issueId}/work-notes`
    ),

    addWorkNote: (machineId, issueId, formData) => apiCall(
      () => apiClient.post(`/machines/${machineId}/issues/${issueId}/work-notes`, formData, {
        headers: formData instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {}
      }),
      `/machines/${machineId}/issues/${issueId}/work-notes`
    ),

    // ============= MONITORING =============

    startMonitoring: (machineId, issueId, formData) => apiCall(
      () => apiClient.post(`/machines/${machineId}/issues/${issueId}/start-monitoring`, formData, {
        headers: formData instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {}
      }),
      `/machines/${machineId}/issues/${issueId}/start-monitoring`
    ),

    confirmMonitoring: (machineId, issueId) => apiCall(
      () => apiClient.post(`/machines/${machineId}/issues/${issueId}/confirm-monitoring`),
      `/machines/${machineId}/issues/${issueId}/confirm-monitoring`
    ),

    markMonitoringIneffective: (machineId, issueId, data) => apiCall(
      () => apiClient.post(`/machines/${machineId}/issues/${issueId}/mark-monitoring-ineffective`, data),
      `/machines/${machineId}/issues/${issueId}/mark-monitoring-ineffective`
    )

  },

  // ============= GLOBAL PARAMETER CATALOG =============
  parameterDefinitions: {
    getAll: (params = {}) => apiCall(
      () => apiClient.get('/parameter-definitions', { params }),
      '/parameter-definitions'
    ),
    create: (data) => apiCall(
      () => apiClient.post('/parameter-definitions', data),
      '/parameter-definitions'
    ),
    update: (defId, data) => apiCall(
      () => apiClient.put(`/parameter-definitions/${defId}`, data),
      `/parameter-definitions/${defId}`
    ),
    delete: (defId) => apiCall(
      () => apiClient.delete(`/parameter-definitions/${defId}`),
      `/parameter-definitions/${defId}`
    ),
  },

  parameterGroups: {
    getTree: () => apiCall(
      () => apiClient.get('/parameter-groups/tree'),
      '/parameter-groups/tree'
    ),
    getAll: () => apiCall(
      () => apiClient.get('/parameter-groups'),
      '/parameter-groups'
    ),
    create: (data) => apiCall(
      () => apiClient.post('/parameter-groups', data),
      '/parameter-groups'
    ),
    update: (groupId, data) => apiCall(
      () => apiClient.put(`/parameter-groups/${groupId}`, data),
      `/parameter-groups/${groupId}`
    ),
    delete: (groupId) => apiCall(
      () => apiClient.delete(`/parameter-groups/${groupId}`),
      `/parameter-groups/${groupId}`
    ),
  },

  // ============= USER MANAGEMENT =============
  users: {
    getAll: (params = {}) => apiCall(
      () => apiClient.get('/users', { params }),
      '/users'
    ),
    getById: (id) => apiCall(
      () => apiClient.get(`/users/${id}`),
      `/users/${id}`
    ),
    create: (data) => apiCall(
      () => apiClient.post('/users', data),
      '/users'
    ),
    update: (id, data) => apiCall(
      () => apiClient.put(`/users/${id}`, data),
      `/users/${id}`
    ),
    delete: (id) => apiCall(
      () => apiClient.delete(`/users/${id}`),
      `/users/${id}`
    ),
    terminate: (id, data) => apiCall(
      () => apiClient.post(`/users/${id}/terminate`, data),
      `/users/${id}/terminate`
    ),
    getUnassigned: () => apiCall(
      () => apiClient.get('/users/status/unassigned'),
      '/users/status/unassigned'
    ),
    getRoleStats: () => apiCall(
      () => apiClient.get('/users/stats/roles'),
      '/users/stats/roles'
    ),

    // NEW: Status management endpoints
    updateMyStatus: (data) => apiCall(
      () => apiClient.put('/users/me/status', data),
      '/users/me/status'
    ),
    getMyAvailability: () => apiCall(
      () => apiClient.get('/users/me/availability'),
      '/users/me/availability'
    ),
    getAvailabilitySummary: () => apiCall(
      () => apiClient.get('/users/availability-summary'),
      '/users/availability-summary'
    ),
    getAvailableForWork: () => apiCall(
      () => apiClient.get('/users/available-for-work'),
      '/users/available-for-work'
    ),
    getUserWorkStatus: (userId) => apiCall(
      () => apiClient.get(`/users/${userId}/work-status`),
      `/users/${userId}/work-status`
    ),
    bulkStatusUpdate: (updates) => apiCall(
      () => apiClient.post('/users/bulk-status-update', { updates }),
      '/users/bulk-status-update'
    ),

    // Lightweight endpoint for dropdowns across modules
    getForDropdown: () => apiCall(
      () => apiClient.get('/users/for-dropdown'),
      '/users/for-dropdown'
    ),

    // Analytics endpoints
    getMyAnalytics: () => apiCall(
      () => apiClient.get('/users/me/analytics'),
      '/users/me/analytics'
    ),
    getUserAnalytics: (userId) => apiCall(
      () => apiClient.get(`/users/${userId}/analytics`),
      `/users/${userId}/analytics`
    )
  },

  // ============= USER STATS (Performance Analytics) =============
  userStats: {
    // Get current user's performance stats
    getMyStats: (params = {}) => apiCall(
      () => apiClient.get('/user-stats/my-stats', { params }),
      '/user-stats/my-stats'
    ),
    
    // Get team stats (current user + direct reports)
    getTeamStats: (params = {}) => apiCall(
      () => apiClient.get('/user-stats/team-stats', { params }),
      '/user-stats/team-stats'
    ),
    
    // Get stats for a specific user
    getUserStats: (userId, params = {}) => apiCall(
      () => apiClient.get(`/user-stats/user/${userId}`, { params }),
      `/user-stats/user/${userId}`
    )
  },

  // ============= RBAC SYSTEM =============
  rbac: {
    // Permissions
    permissions: {
      getAll: (params = {}) => apiCall(
        () => apiClient.get('/rbac/permissions', { params }),
        '/rbac/permissions'
      )
    },
    
    // Roles
    roles: {
      getAll: (params = {}) => apiCall(
        () => apiClient.get('/rbac/roles', { params }),
        '/rbac/roles'
      ),
      getById: (id) => apiCall(
        () => apiClient.get(`/rbac/roles/${id}`),
        `/rbac/roles/${id}`
      ),
      create: (data) => apiCall(
        () => apiClient.post('/rbac/roles', data),
        '/rbac/roles'
      ),
      update: (id, data) => apiCall(
        () => apiClient.put(`/rbac/roles/${id}`, data),
        `/rbac/roles/${id}`
      ),
      delete: (id) => apiCall(
        () => apiClient.delete(`/rbac/roles/${id}`),
        `/rbac/roles/${id}`
      ),
      getApprovalChain: (id) => apiCall(
        () => apiClient.get(`/rbac/roles/${id}/approval-chain`),
        `/rbac/roles/${id}/approval-chain`
      ),
      getApprover: (id) => apiCall(
        () => apiClient.get(`/rbac/roles/${id}/approver`),
        `/rbac/roles/${id}/approver`
      )
    },
    
    // Role Hierarchy
    hierarchy: {
      get: () => apiCall(
        () => apiClient.get('/rbac/hierarchy'),
        '/rbac/hierarchy'
      ),
      getRolesForDropdown: () => apiCall(
        () => apiClient.get('/rbac/roles-for-hierarchy'),
        '/rbac/roles-for-hierarchy'
      )
    },
    
    // User Role Assignments
    users: {
      getRoles: (userId) => apiCall(
        () => apiClient.get(`/rbac/users/${userId}/roles`),
        `/rbac/users/${userId}/roles`
      ),
      assignRole: (userId, data) => apiCall(
        () => apiClient.post(`/rbac/users/${userId}/roles`, data),
        `/rbac/users/${userId}/roles`
      ),
      removeRole: (userId, roleId) => apiCall(
        () => apiClient.delete(`/rbac/users/${userId}/roles/${roleId}`),
        `/rbac/users/${userId}/roles/${roleId}`
      )
    },
    
    // System Status
    getStatus: () => apiCall(
      () => apiClient.get('/rbac/status'),
      '/rbac/status'
    )
  },

  // ============= COMPLIANCE REPORTING (FDA 21 CFR Part 11) =============
  compliance: {
    auditLog: (params = {}) => apiCall(
      () => apiClient.get('/compliance/audit-log', { params }),
      '/compliance/audit-log'
    ),
    exportAuditLog: (params = {}) => apiCall(
      () => apiClient.get('/compliance/audit-log/export', { params }),
      '/compliance/audit-log/export'
    ),
    userAccessReport: () => apiCall(
      () => apiClient.get('/compliance/user-access-report'),
      '/compliance/user-access-report'
    ),
    passwordExpiryReport: () => apiCall(
      () => apiClient.get('/compliance/password-expiry-report'),
      '/compliance/password-expiry-report'
    ),
    loginHistory: (params = {}) => apiCall(
      () => apiClient.get('/compliance/login-history', { params }),
      '/compliance/login-history'
    ),
    // Retention policy management (IATF 16949 clause 7.5.3.2)
    retentionPolicies: () => apiCall(
      () => apiClient.get('/compliance/retention-policies'),
      '/compliance/retention-policies'
    ),
    updateRetentionPolicy: (id, data) => apiCall(
      () => apiClient.put(`/compliance/retention-policies/${id}`, data),
      '/compliance/retention-policies/:id'
    ),
    reviewRetentionPolicy: (id) => apiCall(
      () => apiClient.post(`/compliance/retention-policies/${id}/review`),
      '/compliance/retention-policies/:id/review'
    ),
    retentionStatus: () => apiCall(
      () => apiClient.get('/compliance/retention-status'),
      '/compliance/retention-status'
    ),
    // Encryption status (TISAX AL2)
    encryptionStatus: () => apiCall(
      () => apiClient.get('/compliance/encryption-status'),
      '/compliance/encryption-status'
    )
  },

  // ============= TISAX AL2 — DATA CLASSIFICATION =============
  dataClassification: {
    summary: () => apiCall(
      () => apiClient.get('/data-classification/summary'),
      '/data-classification/summary'
    ),
    get: (entityType, entityId) => apiCall(
      () => apiClient.get(`/data-classification/${entityType}/${entityId}`),
      '/data-classification/get'
    ),
    set: (entityType, entityId, data) => apiCall(
      () => apiClient.put(`/data-classification/${entityType}/${entityId}`, data),
      '/data-classification/set'
    ),
    bulkSet: (data) => apiCall(
      () => apiClient.post('/data-classification/bulk', data),
      '/data-classification/bulk'
    ),
    list: (entityType, params = {}) => apiCall(
      () => apiClient.get(`/data-classification/${entityType}/list`, { params }),
      '/data-classification/list'
    )
  },

  // ============= TISAX AL2 — SECURITY EVENTS =============
  securityEvents: {
    list: (params = {}) => apiCall(
      () => apiClient.get('/security-events', { params }),
      '/security-events'
    ),
    summary: () => apiCall(
      () => apiClient.get('/security-events/summary'),
      '/security-events/summary'
    ),
    acknowledge: (id, data = {}) => apiCall(
      () => apiClient.put(`/security-events/${id}/acknowledge`, data),
      '/security-events/acknowledge'
    ),
    export: (params = {}) => apiCall(
      () => apiClient.get('/security-events/export', { params }),
      '/security-events/export'
    )
  },

  // ============= TISAX AL2 — ACCESS REVIEWS =============
  accessReviews: {
    initiate: (data) => apiCall(
      () => apiClient.post('/access-reviews/initiate', data),
      '/access-reviews/initiate'
    ),
    list: (params = {}) => apiCall(
      () => apiClient.get('/access-reviews', { params }),
      '/access-reviews'
    ),
    dashboard: () => apiCall(
      () => apiClient.get('/access-reviews/dashboard'),
      '/access-reviews/dashboard'
    ),
    update: (id, data) => apiCall(
      () => apiClient.put(`/access-reviews/${id}`, data),
      '/access-reviews/update'
    ),
    sign: (id, data) => apiCall(
      () => apiClient.post(`/access-reviews/${id}/sign`, data),
      '/access-reviews/sign'
    ),
    dormantAccounts: () => apiCall(
      () => apiClient.get('/access-reviews/dormant-accounts'),
      '/access-reviews/dormant-accounts'
    ),
    excessivePrivileges: () => apiCall(
      () => apiClient.get('/access-reviews/excessive-privileges'),
      '/access-reviews/excessive-privileges'
    )
  },

  // ============= SMED SYSTEM =============
  smed: {
    // Overview
    getOverview: () => apiCall(
      () => apiClient.get('/smed/overview'),
      '/smed/overview'
    ),
    
    // Templates
    templates: {
      getAll: (params = {}) => apiCall(
        () => apiClient.get('/smed/templates', { params }),
        '/smed/templates'
      ),
      getById: (templateId) => apiCall(
        () => apiClient.get(`/smed/templates/${templateId}`),
        `/smed/templates/${templateId}`
      ),
      create: (data) => apiCall(
        () => apiClient.post('/smed/templates', data),
        '/smed/templates'
      ),
      update: (templateId, data) => apiCall(
        () => apiClient.put(`/smed/templates/${templateId}`, data),
        `/smed/templates/${templateId}`
      ),
      clone: (templateId, data) => apiCall(
        () => apiClient.post(`/smed/templates/${templateId}/clone`, data),
        `/smed/templates/${templateId}/clone`
      ),
      delete: (templateId) => apiCall(
        () => apiClient.delete(`/smed/templates/${templateId}`),
        `/smed/templates/${templateId}`
      ),
      updateRoleTaskList: (templateId, teamKey, data) => apiCall(
        () => apiClient.put(`/smed/templates/${templateId}/roles/${teamKey}`, data),
        `/smed/templates/${templateId}/roles/${teamKey}`
      ),
      removeRoleTaskList: (templateId, teamKey) => apiCall(
        () => apiClient.delete(`/smed/templates/${templateId}/roles/${teamKey}`),
        `/smed/templates/${templateId}/roles/${teamKey}`
      ),
      getRoleTasks: (templateId, teamKey) => apiCall(
        () => apiClient.get(`/smed/templates/${templateId}/roles/${teamKey}/tasks`),
        `/smed/templates/${templateId}/roles/${teamKey}/tasks`
      ),
      getAvailableRoles: () => apiCall(
        () => apiClient.get('/smed/templates/roles/available'),
        '/smed/templates/roles/available'
      )
    },
    
    // Executions
    executions: {
      getAll: (params = {}) => apiCall(
        () => apiClient.get('/smed/executions', { params }),
        '/smed/executions'
      ),
      getById: (executionId) => apiCall(
        () => apiClient.get(`/smed/executions/${executionId}`),
        `/smed/executions/${executionId}`
      ),
      create: (data) => apiCall(
        () => apiClient.post('/smed/executions', data),
        '/smed/executions'
      ),
      updateTeams: (executionId, data) => apiCall(
        () => apiClient.put(`/smed/executions/${executionId}/teams`, data),
        `/smed/executions/${executionId}/teams`
      ),
      start: (executionId) => apiCall(
        () => apiClient.post(`/smed/executions/${executionId}/start`),
        `/smed/executions/${executionId}/start`
      ),
      getTasks: (executionId, params = {}) => apiCall(
        () => apiClient.get(`/smed/executions/${executionId}/tasks`, { params }),
        `/smed/executions/${executionId}/tasks`
      ),
      updateTask: (executionId, taskId, data) => apiCall(
        () => apiClient.put(`/smed/executions/${executionId}/tasks/${taskId}`, data),
        `/smed/executions/${executionId}/tasks/${taskId}`
      ),
      getUserAssigned: (params = {}) => apiCall(
        () => apiClient.get('/smed/executions/user/assigned', { params }),
        '/smed/executions/user/assigned'
      ),
      complete: (executionId, data = {}) => apiCall(
        () => apiClient.post(`/smed/executions/${executionId}/complete`, data),
        `/smed/executions/${executionId}/complete`
      ),
      cancel: (executionId, data) => apiCall(
        () => apiClient.post(`/smed/executions/${executionId}/cancel`, data),
        `/smed/executions/${executionId}/cancel`
      )
    },
    
    // Setup Sheets
    setupSheets: {
      getAll: (params = {}) => apiCall(
        () => apiClient.get('/smed/setup-sheets', { params }),
        '/smed/setup-sheets'
      ),
      getById: (id) => apiCall(
        () => apiClient.get(`/smed/setup-sheets/${id}`),
        `/smed/setup-sheets/${id}`
      ),
      create: (data) => apiCall(
        () => apiClient.post('/smed/setup-sheets', data),
        '/smed/setup-sheets'
      ),
      update: (id, data) => apiCall(
        () => apiClient.put(`/smed/setup-sheets/${id}`, data),
        `/smed/setup-sheets/${id}`
      ),
      delete: (id) => apiCall(
        () => apiClient.delete(`/smed/setup-sheets/${id}`),
        `/smed/setup-sheets/${id}`
      ),
      getByMoldAndMachine: (moldId, machineId) => apiCall(
        () => apiClient.get(`/smed/setup-sheets/mold/${moldId}/machine/${machineId}`),
        `/smed/setup-sheets/mold/${moldId}/machine/${machineId}`
      ),
      updateRoleParameters: (id, teamKey, data) => apiCall(
        () => apiClient.put(`/smed/setup-sheets/${id}/roles/${teamKey}`, data),
        `/smed/setup-sheets/${id}/roles/${teamKey}`
      ),
      getRoleParameters: (id, teamKey) => apiCall(
        () => apiClient.get(`/smed/setup-sheets/${id}/roles/${teamKey}`),
        `/smed/setup-sheets/${id}/roles/${teamKey}`
      ),
      getByMold: (moldId) => apiCall(
        () => apiClient.get(`/smed/setup-sheets/mold/${moldId}/all`),
        `/smed/setup-sheets/mold/${moldId}/all`
      ),
      getByMachine: (machineId) => apiCall(
        () => apiClient.get(`/smed/setup-sheets/machine/${machineId}/all`),
        `/smed/setup-sheets/machine/${machineId}/all`
      ),
      clone: (id, data) => apiCall(
        () => apiClient.post(`/smed/setup-sheets/${id}/clone`, data),
        `/smed/setup-sheets/${id}/clone`
      ),
      getAvailableRoles: () => apiCall(
        () => apiClient.get('/smed/setup-sheets/roles/available'),
        '/smed/setup-sheets/roles/available'
      )
    }
  },

  // ============= PREVENTIVE MAINTENANCE =============
  pm: {
    getDashboard: (params = {}) => apiCall(
      () => apiClient.get('/pm/dashboard', { params }),
      '/pm/dashboard'
    ),
    
    // Areas
    areas: {
      getAll: (params = {}) => apiCall(
        () => apiClient.get('/pm/areas', { params }),
        '/pm/areas'
      ),
      getById: (id) => apiCall(
        () => apiClient.get(`/pm/areas/${id}`),
        `/pm/areas/${id}`
      ),
      create: (data) => apiCall(
        () => apiClient.post('/pm/areas', data),
        '/pm/areas'
      ),
      update: (id, data) => apiCall(
        () => apiClient.put(`/pm/areas/${id}`, data),
        `/pm/areas/${id}`
      ),
      delete: (id) => apiCall(
        () => apiClient.delete(`/pm/areas/${id}`),
        `/pm/areas/${id}`
      ),
      clone: (id, data) => apiCall(
        () => apiClient.post(`/pm/areas/${id}/clone`, data),
        `/pm/areas/${id}/clone`
      ),
      getSchedules: (id) => apiCall(
        () => apiClient.get(`/pm/areas/${id}/schedules`),
        `/pm/areas/${id}/schedules`
      ),
      updateTools: (id, data) => apiCall(
        () => apiClient.put(`/pm/areas/${id}/tools`, data),
        `/pm/areas/${id}/tools`
      )
    },
    
    // Common data
    common: {
      getDescriptions: () => apiCall(
        () => apiClient.get('/pm/common/descriptions'),
        '/pm/common/descriptions'
      ),
      getTaskDescriptions: () => apiCall(
        () => apiClient.get('/pm/common/task-descriptions'),
        '/pm/common/task-descriptions'
      ),
      getTools: () => apiCall(
        () => apiClient.get('/pm/common/tools'),
        '/pm/common/tools'
      ),
      // Get roles for PM assignment dropdown
      getRoles: () => apiCall(
        () => apiClient.get('/pm/common/roles'),
        '/pm/common/roles'
      )
    },
    
    // Spare parts
    spareParts: {
      search: (params = {}) => apiCall(
        () => apiClient.get('/pm/spare-parts/search', { params }),
        '/pm/spare-parts/search'
      )
    },
    
    // Completions
    completions: {
      getAll: (params = {}) => apiCall(
        () => apiClient.get('/pm/completions', { params }),
        '/pm/completions'
      ),
      create: (data) => apiCall(
        () => apiClient.post('/pm/completions', data),
        '/pm/completions'
      ),
      // Batch completion with FormData (for multiple tasks with images)
      createBatch: (formData) => apiCall(
        () => apiClient.post('/pm/completions/batch', formData),
        '/pm/completions/batch'
      )
    },
    
    // Schedule
    schedule: {
      getAll: (params = {}) => apiCall(
        () => apiClient.get('/pm/schedule', { params }),
        '/pm/schedule'
      ),
      update: (id, data) => apiCall(
        () => apiClient.put(`/pm/schedule/${id}`, data),
        `/pm/schedule/${id}`
      )
    },
    
    // PM Analytics
    analytics: {
      getAll: (params = {}) => apiCall(
        () => apiClient.get('/pm/analytics', { params }),
        '/pm/analytics'
      ),
      getTrends: (params = {}) => apiCall(
        () => apiClient.get('/pm/analytics/trends', { params }),
        '/pm/analytics/trends'
      ),
      export: (params = {}) => apiCall(
        () => apiClient.get('/pm/analytics/export', { params, responseType: 'blob' }),
        '/pm/analytics/export'
      ),
      // NEW: Enhanced analytics for comprehensive dashboard (auditors/COO)
      getEnhanced: (params = {}) => apiCall(
        () => apiClient.get('/pm/analytics/enhanced', { params }),
        '/pm/analytics/enhanced'
      ),
      // NEW: Detailed completion history for audit view
      getCompletionHistory: (params = {}) => apiCall(
        () => apiClient.get('/pm/analytics/completion-history', { params }),
        '/pm/analytics/completion-history'
      )
    },
    
    // File upload
    uploadImage: (file) => apiCall(
      () => {
        const formData = new FormData();
        formData.append('image', file);
        return apiClient.post('/pm/upload-image', formData);
      },
      '/pm/upload-image'
    )
  },


  // ============= SAFETY MANAGEMENT  =============
  safety: {
    // Incidents
    incidents: {
      getAll: (params = {}) => apiCall(
        () => apiClient.get('/safety/incidents', { params }),
        '/safety/incidents'
      ),
      getById: (id) => apiCall(
        () => apiClient.get(`/safety/incidents/${id}`),
        `/safety/incidents/${id}`
      ),
      create: (data) => apiCall(
        () => apiClient.post('/safety/incidents', data),
        '/safety/incidents'
      ),
      update: (id, data) => apiCall(
        () => apiClient.put(`/safety/incidents/${id}`, data),
        `/safety/incidents/${id}`
      ),
      addInvestigation: (id, data) => apiCall(
        () => apiClient.post(`/safety/incidents/${id}/investigation`, data),
        `/safety/incidents/${id}/investigation`
      ),
      // FIXED: Changed from /actions to /corrective-actions
      addAction: (id, data) => apiCall(
        () => apiClient.post(`/safety/incidents/${id}/corrective-actions`, data),
        `/safety/incidents/${id}/corrective-actions`
      ),
      // FIXED: Changed from /actions to /corrective-actions
      updateAction: (incidentId, actionId, data) => apiCall(
        () => apiClient.put(`/safety/incidents/${incidentId}/corrective-actions/${actionId}`, data),
        `/safety/incidents/${incidentId}/corrective-actions/${actionId}`
      ),
      close: (id) => apiCall(
        () => apiClient.put(`/safety/incidents/${id}/close`),
        `/safety/incidents/${id}/close`
      ),
      delete: (id) => apiCall(
        () => apiClient.delete(`/safety/incidents/${id}`),
        `/safety/incidents/${id}`
      ),
      getPhoto: (id, filename) => apiCall(
        () => apiClient.get(`/safety/incidents/${id}/photos/${filename}`, { 
          responseType: 'blob' 
        }),
        `/safety/incidents/${id}/photos/${filename}`
      ),
      export: (params = {}) => apiCall(
        () => apiClient.get('/safety/incidents/export', { params }),
        '/safety/incidents/export'
      )
    },
    
    // Analytics
    getAnalytics: (params = {}) => apiCall(
      () => apiClient.get('/safety/analytics', { params }),
      '/safety/analytics'
    )
  },

  // ============= 6S AUDIT SYSTEM =============
  sixS: {
    // Areas
    areas: {
      getAll: (params = {}) => apiCall(
        () => apiClient.get('/sixs/areas', { params }),
        '/sixs/areas'
      ),
      getById: (id) => apiCall(
        () => apiClient.get(`/sixs/areas/${id}`),
        `/sixs/areas/${id}`
      ),
      create: (data) => apiCall(
        () => apiClient.post('/sixs/areas', data),
        '/sixs/areas'
      ),
      delete: (id) => apiCall(
        () => apiClient.delete(`/sixs/areas/${id}`),
        `/sixs/areas/${id}`
      )
    },
    
    // Audits
    submitAudit: (data) => apiCall(
      () => apiClient.post('/sixs/submit', data),
      '/sixs/submit'
    ),
    getHistory: (areaId, params = {}) => apiCall(
      () => apiClient.get(`/sixs/history/${areaId}`, { params }),
      `/sixs/history/${areaId}`
    ),
    getDashboard: () => apiCall(
      () => apiClient.get('/sixs/dashboard'),
      '/sixs/dashboard'
    )
  },

  // ============= ANALYTICS =============
  analytics: {
    getDashboard: (params = {}) => apiCall(
      () => apiClient.get('/analytics/dashboard', { params }),
      '/analytics/dashboard'
    ),
    getUnifiedDashboard: (params = {}) => apiCall(
      () => apiClient.get('/analytics/unified-dashboard', { params }),
      '/analytics/unified-dashboard'
    )
  },

  // ============= AI CHAT =============
  ai: {
    chat: (message, { conversationId = null } = {}) => apiCall(
      () => apiClient.post('/ai/chat', { message, conversationId }),
      '/ai/chat'
    ),
    chatStream: (message, { conversationId = null } = {}) =>
      fetch(`${API_BASE_URL}/ai/chat/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, conversationId })
      }),
    getResolutionSuggestions: ({ issueId, machineId }) => apiCall(
      () => apiClient.post('/ai/resolution-suggestions', { issueId, machineId }),
      '/ai/resolution-suggestions'
    ),
    getInsightsSummary: (params = {}) => apiCall(
      () => apiClient.get('/ai/insights/summary', { params }),
      '/ai/insights/summary'
    ),
    getSuggestions: (intent) => apiCall(
      () => apiClient.get(`/ai/suggestions/${intent}`),
      `/ai/suggestions/${intent}`
    ),
    analyze: (data) => apiCall(
      () => apiClient.post('/ai/analyze', data),
      '/ai/analyze'
    ),
    getHealth: () => apiCall(
      () => apiClient.get('/ai/health'),
      '/ai/health'
    ),
    // Merlin Knowledge management (editable system prompt sections)
    knowledge: {
      getAll: () => apiCall(
        () => apiClient.get('/ai/knowledge'),
        '/ai/knowledge'
      ),
      getById: (id) => apiCall(
        () => apiClient.get(`/ai/knowledge/${id}`),
        `/ai/knowledge/${id}`
      ),
      create: (data) => apiCall(
        () => apiClient.post('/ai/knowledge', data),
        '/ai/knowledge'
      ),
      update: (id, data) => apiCall(
        () => apiClient.put(`/ai/knowledge/${id}`, data),
        `/ai/knowledge/${id}`
      ),
      delete: (id) => apiCall(
        () => apiClient.delete(`/ai/knowledge/${id}`),
        `/ai/knowledge/${id}`
      ),
      // Knowledge documents (PDFs processed through RAG pipeline)
      documents: {
        list: () => apiCall(
          () => apiClient.get('/ai/knowledge/documents'),
          '/ai/knowledge/documents'
        ),
        upload: (formData) => apiCall(
          () => apiClient.post(
            '/ai/knowledge/documents',
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          ),
          '/ai/knowledge/documents'
        ),
        delete: (docId) => apiCall(
          () => apiClient.delete(`/ai/knowledge/documents/${docId}`),
          `/ai/knowledge/documents/${docId}`
        ),
        reprocess: (docId) => apiCall(
          () => apiClient.post(`/ai/knowledge/documents/${docId}/reprocess`),
          `/ai/knowledge/documents/${docId}/reprocess`
        ),
        getStatus: (docId) => apiCall(
          () => apiClient.get(`/ai/knowledge/documents/${docId}/status`),
          `/ai/knowledge/documents/${docId}/status`
        )
      }
    },
    // Conversation persistence (Phase 2)
    conversations: {
      list: (params = {}) => apiCall(
        () => apiClient.get('/ai/conversations', { params }),
        '/ai/conversations'
      ),
      create: () => apiCall(
        () => apiClient.post('/ai/conversations'),
        '/ai/conversations'
      ),
      getMessages: (conversationId, params = {}) => apiCall(
        () => apiClient.get(`/ai/conversations/${conversationId}/messages`, { params }),
        `/ai/conversations/${conversationId}/messages`
      ),
      archive: (conversationId) => apiCall(
        () => apiClient.patch(`/ai/conversations/${conversationId}`, { action: 'archive' }),
        `/ai/conversations/${conversationId}`
      ),
      rename: (conversationId, title) => apiCall(
        () => apiClient.patch(`/ai/conversations/${conversationId}`, { title }),
        `/ai/conversations/${conversationId}`
      )
    },
    // Usage tracking (superadmin)
    usage: {
      getSummary: (params = {}) => apiCall(
        () => apiClient.get('/ai/usage', { params }),
        '/ai/usage'
      ),
      getConversationCost: (conversationId) => apiCall(
        () => apiClient.get(`/ai/usage/conversation/${conversationId}`),
        `/ai/usage/conversation/${conversationId}`
      )
    },
    // LLM configuration (admin)
    llmConfig: {
      get: () => apiCall(
        () => apiClient.get('/ai/llm-config'),
        '/ai/llm-config'
      ),
      updateModels: (assignments) => apiCall(
        () => apiClient.put('/ai/llm-config/models', { assignments }),
        '/ai/llm-config/models'
      ),
      updateProvider: (provider, data) => apiCall(
        () => apiClient.put(`/ai/llm-config/provider/${provider}`, data),
        `/ai/llm-config/provider/${provider}`
      ),
      testProvider: (provider, data) => apiCall(
        () => apiClient.post(`/ai/llm-config/provider/${provider}/test`, data),
        `/ai/llm-config/provider/${provider}/test`
      )
    }
  },

  // ============= DIAGNOSTIC TREES =============
  diagnosticTrees: {
    getAll: (params = {}) => apiCall(
      () => apiClient.get('/diagnostic-trees', { params }),
      '/diagnostic-trees'
    ),
    getById: (id) => apiCall(
      () => apiClient.get(`/diagnostic-trees/${id}`),
      `/diagnostic-trees/${id}`
    ),
    create: (data) => apiCall(
      () => apiClient.post('/diagnostic-trees', data),
      '/diagnostic-trees'
    ),
    update: (id, data) => apiCall(
      () => apiClient.put(`/diagnostic-trees/${id}`, data),
      `/diagnostic-trees/${id}`
    ),
    delete: (id) => apiCall(
      () => apiClient.delete(`/diagnostic-trees/${id}`),
      `/diagnostic-trees/${id}`
    ),
    addNode: (id, data) => apiCall(
      () => apiClient.post(`/diagnostic-trees/${id}/nodes`, data),
      `/diagnostic-trees/${id}/nodes`
    ),
    updateNode: (id, nodeId, data) => apiCall(
      () => apiClient.put(`/diagnostic-trees/${id}/nodes/${nodeId}`, data),
      `/diagnostic-trees/${id}/nodes/${nodeId}`
    ),
    deleteNode: (id, nodeId) => apiCall(
      () => apiClient.delete(`/diagnostic-trees/${id}/nodes/${nodeId}`),
      `/diagnostic-trees/${id}/nodes/${nodeId}`
    ),
    reorderNodes: (id, data) => apiCall(
      () => apiClient.put(`/diagnostic-trees/${id}/nodes/reorder`, data),
      `/diagnostic-trees/${id}/nodes/reorder`
    )
  },

  // ============= KAIZEN SYSTEM =============
  kaizen: {
    // Get all kaizen suggestions with filtering and pagination
    getAll: (params = {}) => apiCall(
      () => apiClient.get('/kaizen', { params }),
      '/kaizen'
    ),
    
    // Get single kaizen suggestion by ID
    getById: (id) => apiCall(
      () => apiClient.get(`/kaizen/${id}`),
      `/kaizen/${id}`
    ),
    
    // Create new kaizen suggestion
    create: (data) => apiCall(
      () => apiClient.post('/kaizen', data),
      '/kaizen'
    ),
    
    // Vote on kaizen suggestion (like, love, dislike)
    vote: (id, voteType) => apiCall(
      () => apiClient.post(`/kaizen/${id}/vote`, { voteType }),
      `/kaizen/${id}/vote`
    ),
    
    // Update kaizen status (approve, reject, start implementation)
    updateStatus: (id, data) => apiCall(
      () => apiClient.put(`/kaizen/${id}/status`, data),
      `/kaizen/${id}/status`
    ),
    
    // Delete kaizen suggestion
    delete: (id) => apiCall(
      () => apiClient.delete(`/kaizen/${id}`),
      `/kaizen/${id}`
    ),
    
    // Get kaizen statistics and metrics
    getStats: () => apiCall(
      () => apiClient.get('/kaizen/stats'),
      '/kaizen/stats'
    ),
    
    // Get top contributors leaderboard
    getTopContributors: () => apiCall(
      () => apiClient.get('/kaizen/contributors'),
      '/kaizen/contributors'
    )
  },

  // ============= COMPANY HOLIDAYS =============
  holidays: {
    // Get holidays within a date range
    getAll: (params = {}) => apiCall(
      () => apiClient.get('/holidays', { params }),
      '/holidays'
    ),
    
    // Get holidays for a specific year (includes recurring)
    getByYear: (year) => apiCall(
      () => apiClient.get(`/holidays/year/${year}`),
      `/holidays/year/${year}`
    ),
    
    // Get recurring holiday templates only
    getRecurring: () => apiCall(
      () => apiClient.get('/holidays/recurring'),
      '/holidays/recurring'
    ),
    
    // Get single holiday
    getById: (id) => apiCall(
      () => apiClient.get(`/holidays/${id}`),
      `/holidays/${id}`
    ),
    
    // Create new holiday (requires MANAGE_HR)
    create: (data) => apiCall(
      () => apiClient.post('/holidays', data),
      '/holidays'
    ),
    
    // Update holiday (requires MANAGE_HR)
    update: (id, data) => apiCall(
      () => apiClient.put(`/holidays/${id}`, data),
      `/holidays/${id}`
    ),
    
    // Delete holiday (requires MANAGE_HR)
    delete: (id) => apiCall(
      () => apiClient.delete(`/holidays/${id}`),
      `/holidays/${id}`
    ),
    
    // Bulk create holidays (requires MANAGE_HR)
    bulkCreate: (data) => apiCall(
      () => apiClient.post('/holidays/bulk', data),
      '/holidays/bulk'
    )
  },

  // ============= LEAVE MANAGEMENT =============
  leave: {
    // Apply for leave
    apply: (data) => apiCall(
      () => apiClient.post('/leave/apply', data),
      '/leave/apply'
    ),
    
    // Get leave requests with filtering
    getAll: (params = {}) => apiCall(
      () => apiClient.get('/leave', { params }),
      '/leave'
    ),
    
    // Get eligible users for leave application (requires APPROVE_LEAVE)
    getEligibleUsers: () => apiCall(
      () => apiClient.get('/leave/eligible-users'),
      '/leave/eligible-users'
    ),
    
    // Get peer users (same role) for calendar filter - no special permission needed
    getPeers: () => apiCall(
      () => apiClient.get('/leave/peers'),
      '/leave/peers'
    ),
    
    // Get my approvers based on role hierarchy
    getMyApprovers: () => apiCall(
      () => apiClient.get('/leave/my-approvers'),
      '/leave/my-approvers'
    ),
    
    // Get pending approvals that I can approve (based on role hierarchy)
    getPendingApprovals: () => apiCall(
      () => apiClient.get('/leave/pending-approvals'),
      '/leave/pending-approvals'
    ),
    
    // Get calendar view of leaves (requires VIEW_LEAVE_CALENDAR permission)
    getCalendar: (params = {}) => apiCall(
      () => apiClient.get('/leave/calendar', { params }),
      '/leave/calendar'
    ),
    
    // Get peer calendar view (own leaves + peers with same role) - no special permission needed
    getPeerCalendar: (params = {}) => apiCall(
      () => apiClient.get('/leave/peer-calendar', { params }),
      '/leave/peer-calendar'
    ),
    
    // Update leave status (approve/deny)
    updateStatus: (leaveId, data) => apiCall(
      () => apiClient.put(`/leave/${leaveId}/status`, data),
      `/leave/${leaveId}/status`
    ),
    
    // Update leave request
    update: (leaveId, data) => apiCall(
      () => apiClient.put(`/leave/${leaveId}`, data),
      `/leave/${leaveId}`
    ),
    
    // Cancel leave request
    cancel: (leaveId) => apiCall(
      () => apiClient.delete(`/leave/${leaveId}`),
      `/leave/${leaveId}`
    ),
    
    // Check for conflicting leave requests
    checkConflicts: (data) => apiCall(
      () => apiClient.post('/leave/check-conflicts', data),
      '/leave/check-conflicts'
    ),
    
    // Calculate PTO days (excludes weekends and holidays)
    calculateDays: (data) => apiCall(
      () => apiClient.post('/leave/calculate-days', data),
      '/leave/calculate-days'
    ),
    
    // Get all active roles for calendar filtering (lightweight, no special permission)
    getRoles: () => apiCall(
      () => apiClient.get('/leave/roles'),
      '/leave/roles'
    )
  },

  // ============= LEAVE SETTINGS & PROFILES =============
  leaveSettings: {
    // Get company-wide leave settings
    getSettings: () => apiCall(
      () => apiClient.get('/leave-settings'),
      '/leave-settings'
    ),
    
    // Update company-wide settings (HR only)
    updateSettings: (data) => apiCall(
      () => apiClient.put('/leave-settings', data),
      '/leave-settings'
    ),
    
    // Get all employee profiles (HR only)
    getProfiles: (params = {}) => apiCall(
      () => apiClient.get('/leave-settings/profiles', { params }),
      '/leave-settings/profiles'
    ),
    
    // Get current user's profile
    getMyProfile: () => apiCall(
      () => apiClient.get('/leave-settings/profiles/me'),
      '/leave-settings/profiles/me'
    ),
    
    // Get specific employee's profile
    getProfile: (userId) => apiCall(
      () => apiClient.get(`/leave-settings/profiles/${userId}`),
      `/leave-settings/profiles/${userId}`
    ),
    
    // Update employee's profile (HR only)
    updateProfile: (userId, data) => apiCall(
      () => apiClient.put(`/leave-settings/profiles/${userId}`, data),
      `/leave-settings/profiles/${userId}`
    ),
    
    // Add HR adjustment
    addAdjustment: (userId, data) => apiCall(
      () => apiClient.post(`/leave-settings/profiles/${userId}/adjustments`, data),
      `/leave-settings/profiles/${userId}/adjustments`
    ),
    
    // Get adjustment history
    getAdjustments: (userId, params = {}) => apiCall(
      () => apiClient.get(`/leave-settings/profiles/${userId}/adjustments`, { params }),
      `/leave-settings/profiles/${userId}/adjustments`
    ),
    
    // Add manager override
    addManagerOverride: (userId, data) => apiCall(
      () => apiClient.post(`/leave-settings/profiles/${userId}/manager-override`, data),
      `/leave-settings/profiles/${userId}/manager-override`
    ),
    
    // Get current user's balances
    getMyBalances: (params = {}) => apiCall(
      () => apiClient.get('/leave-settings/balances/me', { params }),
      '/leave-settings/balances/me'
    ),
    
    // Get employee's balances
    getBalances: (userId, params = {}) => apiCall(
      () => apiClient.get(`/leave-settings/balances/${userId}`, { params }),
      `/leave-settings/balances/${userId}`
    ),
    
    // Recalculate balances (HR only)
    recalculateBalances: (data = {}) => apiCall(
      () => apiClient.post('/leave-settings/balances/recalculate', data),
      '/leave-settings/balances/recalculate'
    )
  },

  // ============= SIMPLIFIED CHANGEOVER MANAGEMENT =============
  changeovers: {
    // Get all changeovers with filtering
    getAll: (params = {}) => apiCall(
      () => apiClient.get('/changeovers', { params }),
      '/changeovers'
    ),
    
    // Get dashboard data
    dashboard: () => apiCall(
      () => apiClient.get('/changeovers/dashboard'),
      '/changeovers/dashboard'
    ),
    

    
    // Get specific changeover
    getById: (changeoverId) => apiCall(
      () => apiClient.get(`/changeovers/${changeoverId}`),
      `/changeovers/${changeoverId}`
    ),
    
    // Create new changeover
    create: async (data) => {
      const result = await apiCall(
        () => apiClient.post('/changeovers', data),
        '/changeovers'
      );
      // Trigger dashboard refresh after changeover creation
      window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
        detail: { reason: 'changeoverCreated' }
      }));
      return result;
    },
    
    // Start changeover
    start: async (changeoverId, data = {}) => {
      const result = await apiCall(
        () => apiClient.post(`/changeovers/${changeoverId}/start`, data),
        `/changeovers/${changeoverId}/start`
      );
      // Trigger dashboard refresh after changeover start
      window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
        detail: { reason: 'changeoverStarted', changeoverId }
      }));
      return result;
    },
    
    // Complete changeover
    complete: async (changeoverId, data = {}) => {
      const result = await apiCall(
        () => apiClient.post(`/changeovers/${changeoverId}/complete`, data),
        `/changeovers/${changeoverId}/complete`
      );
      // Trigger dashboard refresh after changeover completion
      window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
        detail: { reason: 'changeoverCompleted', changeoverId }
      }));
      return result;
    },
    
    // Update changeover
    update: async (changeoverId, data) => {
      const result = await apiCall(
        () => apiClient.put(`/changeovers/${changeoverId}`, data),
        `/changeovers/${changeoverId}`
      );
      // Trigger dashboard refresh after changeover update
      window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
        detail: { reason: 'changeoverUpdated', changeoverId }
      }));
      return result;
    },
    
    // Cancel/delete changeover (requires electronic signature)
    delete: async (changeoverId, sigFields = {}) => {
      const result = await apiCall(
        () => apiClient.delete(`/changeovers/${changeoverId}`, { data: sigFields }),
        `/changeovers/${changeoverId}`
      );
      // Trigger dashboard refresh after changeover deletion
      window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
        detail: { reason: 'changeoverCancelled', changeoverId }
      }));
      return result;
    },
    
    // Get active changeovers for a machine
    getActiveForMachine: (machineId) => apiCall(
      () => apiClient.get(`/changeovers/machine/${machineId}/active`),
      `/changeovers/machine/${machineId}/active`
    ),
    
    // Get analytics summary
    getAnalytics: (params = {}) => apiCall(
      () => apiClient.get('/changeovers/analytics/summary', { params }),
      '/changeovers/analytics/summary'
    ),
    
    // Get machine's current mold
    getMachineCurrentMold: (machineId) => apiCall(
      () => apiClient.get(`/changeovers/machines/${machineId}/current-mold`),
      `/changeovers/machines/${machineId}/current-mold`
    ),
    
    // Checklist operations
    getChecklist: (changeoverId) => apiCall(
      () => apiClient.get(`/changeovers/${changeoverId}/checklist`),
      `/changeovers/${changeoverId}/checklist`
    ),
    
    startChecklist: async (changeoverId) => {
      const result = await apiCall(
        () => apiClient.post(`/changeovers/${changeoverId}/checklist/start`),
        `/changeovers/${changeoverId}/checklist/start`
      );
      // Trigger dashboard refresh
      window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
        detail: { reason: 'checklistStarted', changeoverId }
      }));
      return result;
    },
    
    updateChecklistItem: async (changeoverId, itemId, data) => {
      const result = await apiCall(
        () => apiClient.put(`/changeovers/${changeoverId}/checklist/items/${itemId}`, data),
        `/changeovers/${changeoverId}/checklist/items/${itemId}`
      );
      // Trigger dashboard refresh if needed
      if (data.completed) {
        window.dispatchEvent(new CustomEvent('dashboardRefreshRequested', {
          detail: { reason: 'checklistItemCompleted', changeoverId }
        }));
      }
      return result;
    }
  },

  // ============= CHECKLIST TEMPLATE MANAGEMENT =============
  checklistTemplates: {
    // Get roles for template assignment (lightweight, MANAGE_SMED only)
    getRoles: () => apiCall(
      () => apiClient.get('/checklist-templates/roles'),
      '/checklist-templates/roles'
    ),

    // Get all template versions with filtering
    getAll: (params = {}) => apiCall(
      () => apiClient.get('/checklist-templates', { params }),
      '/checklist-templates'
    ),
    
    // Get current active template
    getActive: () => apiCall(
      () => apiClient.get('/checklist-templates/active'),
      '/checklist-templates/active'
    ),
    
    // Get specific template by ID
    getById: (id) => apiCall(
      () => apiClient.get(`/checklist-templates/${id}`),
      `/checklist-templates/${id}`
    ),
    
    // Create new template (deactivates previous)
    create: (data) => apiCall(
      () => apiClient.post('/checklist-templates', data),
      '/checklist-templates'
    ),
    
    // Update template (creates new version)
    update: (id, data) => apiCall(
      () => apiClient.put(`/checklist-templates/${id}`, data),
      `/checklist-templates/${id}`
    ),
    
    // Activate a specific template version
    activate: (id) => apiCall(
      () => apiClient.post(`/checklist-templates/${id}/activate`),
      `/checklist-templates/${id}/activate`
    ),
    
    // Delete template (only if unused)
    delete: (id) => apiCall(
      () => apiClient.delete(`/checklist-templates/${id}`),
      `/checklist-templates/${id}`
    ),
    
    // Get items for specific role
    getItemsForRole: (id, roleId) => apiCall(
      () => apiClient.get(`/checklist-templates/${id}/items/by-role/${roleId}`),
      `/checklist-templates/${id}/items/by-role/${roleId}`
    ),
    
    // Get items for current user
    getItemsForUser: (id) => apiCall(
      () => apiClient.get(`/checklist-templates/${id}/items/for-user`),
      `/checklist-templates/${id}/items/for-user`
    ),
    
    // Preview generated checklist for specific machine/mold
    preview: (id, params = {}) => apiCall(
      () => apiClient.get(`/checklist-templates/${id}/preview`, { params }),
      `/checklist-templates/${id}/preview`
    )
  },

  // ============= MOLD MANAGEMENT =============
  molds: {
    getAll: (params = {}) => apiCall(
      () => apiClient.get('/molds', { params }),
      '/molds'
    ),
    getByMachine: (machineId) => apiCall(
      () => apiClient.get(`/molds/machine/${machineId}`),
      `/molds/machine/${machineId}`
    ),
    create: (data) => apiCall(
      () => apiClient.post('/molds', data),
      '/molds'
    ),
    update: (moldId, data) => apiCall(
      () => apiClient.put(`/molds/${moldId}`, data),
      `/molds/${moldId}`
    ),
    delete: (moldId) => apiCall(
      () => apiClient.delete(`/molds/${moldId}`),
      `/molds/${moldId}`
    )
  },

  // ============= PART MANAGEMENT =============
  parts: {
    getAll: (params = {}) => apiCall(
      () => apiClient.get('/parts', { params }),
      '/parts'
    ),
    create: (data) => apiCall(
      () => apiClient.post('/parts', data),
      '/parts'
    ),
    update: (partId, data) => apiCall(
      () => apiClient.put(`/parts/${partId}`, data),
      `/parts/${partId}`
    ),
    delete: (partId) => apiCall(
      () => apiClient.delete(`/parts/${partId}`),
      `/parts/${partId}`
    )
  },

  // ============= PRODUCTION LINE TIMELINE =============
  timeline: {
    getEvents: (params = {}) => apiCall(
      () => apiClient.get('/timeline', { params }),
      '/timeline'
    )
  },

  // ============= RESOLUTION OPTIONS MANAGEMENT =============
  resolution: {
    // Root Causes
    rootCauses: {
      getAll: (params = {}) => apiCall(
        () => apiClient.get('/resolution/root-causes', { params }),
        '/resolution/root-causes'
      ),
      create: (data) => apiCall(
        () => apiClient.post('/resolution/root-causes', data),
        '/resolution/root-causes'
      ),
      update: (id, data) => apiCall(
        () => apiClient.put(`/resolution/root-causes/${id}`, data),
        `/resolution/root-causes/${id}`
      ),
      delete: (id) => apiCall(
        () => apiClient.delete(`/resolution/root-causes/${id}`),
        `/resolution/root-causes/${id}`
      ),
      bulkCreate: (data) => apiCall(
        () => apiClient.post('/resolution/root-causes/bulk', data),
        '/resolution/root-causes/bulk'
      )
    },
    // Actions Taken
    actionsTaken: {
      getAll: (params = {}) => apiCall(
        () => apiClient.get('/resolution/actions-taken', { params }),
        '/resolution/actions-taken'
      ),
      create: (data) => apiCall(
        () => apiClient.post('/resolution/actions-taken', data),
        '/resolution/actions-taken'
      ),
      update: (id, data) => apiCall(
        () => apiClient.put(`/resolution/actions-taken/${id}`, data),
        `/resolution/actions-taken/${id}`
      ),
      delete: (id) => apiCall(
        () => apiClient.delete(`/resolution/actions-taken/${id}`),
        `/resolution/actions-taken/${id}`
      ),
      bulkCreate: (data) => apiCall(
        () => apiClient.post('/resolution/actions-taken/bulk', data),
        '/resolution/actions-taken/bulk'
      )
    }
  },

  // ============= LONG-TERM REASONS =============
  longTermReasons: {
    // Get all long-term reasons
    getAll: (params = {}) => apiCall(
      () => apiClient.get('/long-term-reasons', { params }),
      '/long-term-reasons'
    ),
    
    // Create new reason
    create: (data) => apiCall(
      () => apiClient.post('/long-term-reasons', data),
      '/long-term-reasons'
    ),
    
    // Update reason
    update: (id, data) => apiCall(
      () => apiClient.put(`/long-term-reasons/${id}`, data),
      `/long-term-reasons/${id}`
    ),
    
    // Delete reason
    delete: (id) => apiCall(
      () => apiClient.delete(`/long-term-reasons/${id}`),
      `/long-term-reasons/${id}`
    ),
    
    // Bulk create reasons
    bulkCreate: (data) => apiCall(
      () => apiClient.post('/long-term-reasons/bulk', data),
      '/long-term-reasons/bulk'
    )
  },

  // ============= HELP REQUEST REASONS =============
  helpRequestReasons: {
    getAll: (params = {}) => apiCall(
      () => apiClient.get('/help-request-reasons', { params }),
      '/help-request-reasons'
    ),
    create: (data) => apiCall(
      () => apiClient.post('/help-request-reasons', data),
      '/help-request-reasons'
    ),
    update: (id, data) => apiCall(
      () => apiClient.put(`/help-request-reasons/${id}`, data),
      `/help-request-reasons/${id}`
    ),
    delete: (id) => apiCall(
      () => apiClient.delete(`/help-request-reasons/${id}`),
      `/help-request-reasons/${id}`
    )
  },

  // ============= PROBLEM MANAGEMENT =============
  problems: {
    getAll: () => apiCall(
      () => apiClient.get('/problems'),
      '/problems'
    ),
    create: (data) => apiCall(
      () => apiClient.post('/problems', data),
      '/problems'
    ),
    update: (problemId, data) => apiCall(
      () => apiClient.put(`/problems/${problemId}`, data),
      `/problems/${problemId}`
    ),
    delete: (problemId) => apiCall(
      () => apiClient.delete(`/problems/${problemId}`),
      `/problems/${problemId}`
    ),
    bulkCreate: (data) => apiCall(
      () => apiClient.post('/problems/bulk', data),
      '/problems/bulk'
    )
  },

  // ============= QUALITY MANAGEMENT =============
  quality: {
    // Dashboard summary
    getDashboard: () => apiCall(
      () => apiClient.get('/quality/dashboard'),
      '/quality/dashboard'
    ),
    
    // Issues needing follow-up (incomplete hold info)
    getIssuesNeedingFollowup: () => apiCall(
      () => apiClient.get('/quality/issues/needs-followup'),
      '/quality/issues/needs-followup'
    ),
    
    // Issues not on hold (with reason)
    getIssuesNotOnHold: () => apiCall(
      () => apiClient.get('/quality/issues/not-on-hold'),
      '/quality/issues/not-on-hold'
    ),
    
    // Issues pending disposition
    getIssuesPendingDisposition: () => apiCall(
      () => apiClient.get('/quality/issues/pending-disposition'),
      '/quality/issues/pending-disposition'
    ),
    
    // Get detailed quality hold info for an issue
    getIssueQualityHold: (issueId) => apiCall(
      () => apiClient.get(`/quality/issues/${issueId}`),
      `/quality/issues/${issueId}`
    ),
    
    // Disposition a quality hold
    dispositionHold: (issueId, data) => apiCall(
      () => apiClient.put(`/quality/issues/${issueId}/disposition`, data),
      `/quality/issues/${issueId}/disposition`
    ),
    
    // Update quality hold info on a machine issue
    updateQualityHold: (machineId, issueId, data) => apiCall(
      () => apiClient.put(`/machines/${machineId}/issues/${issueId}/quality-hold`, data),
      `/machines/${machineId}/issues/${issueId}/quality-hold`
    ),
    
    // Get molds with parts for quality hold modal
    getMoldsWithParts: (machineId) => apiCall(
      () => apiClient.get(`/machines/${machineId}/molds-with-parts`),
      `/machines/${machineId}/molds-with-parts`
    ),
    
    // Get parts with active quality issues for a specific problem (for duplicate prevention)
    getActiveQualityIssues: (machineId, description, moldId = null) => apiCall(
      () => apiClient.get(`/machines/${machineId}/active-quality-issues`, {
        params: { description, moldId }
      }),
      `/machines/${machineId}/active-quality-issues`
    ),
    
    // Analytics
    getAnalytics: (params = {}) => apiCall(
      () => apiClient.get('/quality/analytics', { params }),
      '/quality/analytics'
    ),
    
    // Recent dispositions
    getRecentDispositions: (params = {}) => apiCall(
      () => apiClient.get('/quality/analytics/recent-dispositions', { params }),
      '/quality/analytics/recent-dispositions'
    )
  },

  // ============= RANKING MANAGEMENT =============
  rankings: {
    getAll: () => apiCall(
      () => apiClient.get('/rankings'),
      '/rankings'
    ),
    update: (data) => apiCall(
      () => apiClient.put('/rankings', data),
      '/rankings'
    ),
    getAudit: (params = {}) => apiCall(
      () => apiClient.get('/rankings/audit', { params }),
      '/rankings/audit'
    ),
    getByMachine: (machineId) => apiCall(
      () => apiClient.get(`/rankings/machine/${machineId}`),
      `/rankings/machine/${machineId}`
    )
  },

  // ============= NOTIFICATIONS =============
  notifications: {
    getTelegramStatus: () => apiCall(
      () => apiClient.get('/notifications/telegram/status'),
      '/notifications/telegram/status'
    ),
    clearFailedTelegram: () => apiCall(
      () => apiClient.post('/notifications/telegram/clear-failed'),
      '/notifications/telegram/clear-failed'
    ),
    reinitializeTelegram: () => apiCall(
      () => apiClient.post('/notifications/telegram/reinitialize'),
      '/notifications/telegram/reinitialize'
    )
  },


  // ============= PURCHASE REQUESTS =============
  purchaseRequests: {
    // Get all purchase requests with filtering
    getAll: (params = {}) => apiCall(
      () => apiClient.get('/purchase-requests', { params }),
      '/purchase-requests'
    ),
    
    // Get single purchase request by ID
    getById: (id) => apiCall(
      () => apiClient.get(`/purchase-requests/${id}`),
      `/purchase-requests/${id}`
    ),
    
    // Create new purchase request
    create: (data) => apiCall(
      () => apiClient.post('/purchase-requests', data),
      '/purchase-requests'
    ),
    
    // Update purchase request (basic info only, pending requests)
    update: (id, data) => apiCall(
      () => apiClient.put(`/purchase-requests/${id}`, data),
      `/purchase-requests/${id}`
    ),
    
    // Mark as ordered (purchasers only)
    markAsOrdered: (id, data) => apiCall(
      () => apiClient.put(`/purchase-requests/${id}/order`, data),
      `/purchase-requests/${id}/order`
    ),
    
    // Mark as received (purchasers only)
    markAsReceived: (id, data) => apiCall(
      () => apiClient.put(`/purchase-requests/${id}/receive`, data),
      `/purchase-requests/${id}/receive`
    ),
    
    // Cancel purchase request
    cancel: (id, data) => apiCall(
      () => apiClient.put(`/purchase-requests/${id}/cancel`, data),
      `/purchase-requests/${id}/cancel`
    ),
    
    // Get statistics
    getStats: (params = {}) => apiCall(
      () => apiClient.get('/purchase-requests/stats', { params }),
      '/purchase-requests/stats'
    )
  },

  // ============= HELP ARTICLES =============
  help: {
    // Public methods (all authenticated users)
    search: (query, limit = 10) => apiCall(
      () => apiClient.get('/help/search', { params: { q: query, limit } }),
      '/help/search'
    ),
    
    getCategories: () => apiCall(
      () => apiClient.get('/help/categories'),
      '/help/categories'
    ),
    
    getArticles: (category = null) => apiCall(
      () => apiClient.get('/help/articles', { params: category ? { category } : {} }),
      '/help/articles'
    ),
    
    getBySlug: (slug) => apiCall(
      () => apiClient.get(`/help/articles/${slug}`),
      `/help/articles/${slug}`
    ),
    
    getForPage: (pageRoute) => apiCall(
      () => apiClient.get(`/help/page${pageRoute}`),
      `/help/page${pageRoute}`
    ),

    // Admin methods
    admin: {
      getAll: (params = {}) => apiCall(
        () => apiClient.get('/help/admin/articles', { params }),
        '/help/admin/articles'
      ),
      
      getById: (id) => apiCall(
        () => apiClient.get(`/help/admin/articles/${id}`),
        `/help/admin/articles/${id}`
      ),
      
      create: (data) => apiCall(
        () => apiClient.post('/help/admin/articles', data),
        '/help/admin/articles'
      ),
      
      update: (id, data) => apiCall(
        () => apiClient.put(`/help/admin/articles/${id}`, data),
        `/help/admin/articles/${id}`
      ),
      
      delete: (id) => apiCall(
        () => apiClient.delete(`/help/admin/articles/${id}`),
        `/help/admin/articles/${id}`
      ),
      
      uploadScreenshot: (articleId, formData) => apiCall(
        () => apiClient.post(`/help/admin/articles/${articleId}/screenshot`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        }),
        `/help/admin/articles/${articleId}/screenshot`
      ),
      
      deleteScreenshot: (cloudinaryId) => apiCall(
        () => apiClient.delete(`/help/admin/screenshot/${cloudinaryId}`),
        `/help/admin/screenshot/${cloudinaryId}`
      ),
      
      reorder: (category, articleIds) => apiCall(
        () => apiClient.put('/help/admin/articles/reorder', { category, articleIds }),
        '/help/admin/articles/reorder'
      )
    }
  },

  // ============= TRAINING MANAGEMENT =============
  training: {
    // Get all trainings (with filters)
    getAll: (params = {}) => apiCall(
      () => apiClient.get('/training', { params }),
      '/training'
    ),

    // Get trainings for dropdown (lightweight: _id, title, category only)
    getForDropdown: () => apiCall(
      () => apiClient.get('/training/for-dropdown'),
      '/training/for-dropdown'
    ),

    // Get single training by ID
    getById: (id, includeAnswers = false) => apiCall(
      () => apiClient.get(`/training/${id}`, { params: { includeAnswers } }),
      `/training/${id}`
    ),
    
    // Create new training
    create: (formData) => apiCall(
      () => apiClient.post('/training', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }),
      '/training'
    ),
    
    // Update training
    update: (id, formData) => apiCall(
      () => apiClient.put(`/training/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }),
      `/training/${id}`
    ),
    
    // Publish/unpublish training
    publish: (id, publish = true) => apiCall(
      () => apiClient.put(`/training/${id}/publish`, { publish }),
      `/training/${id}/publish`
    ),
    
    // Delete training
    delete: (id) => apiCall(
      () => apiClient.delete(`/training/${id}`),
      `/training/${id}`
    ),
    
    // Get available frequency options
    getFrequencies: () => apiCall(
      () => apiClient.get('/training/frequencies'),
      '/training/frequencies'
    ),
    
    // ============= ASSIGNMENTS =============
    assignments: {
      // Get current user's assignments
      getMy: (params = {}) => apiCall(
        () => apiClient.get('/training/assignments/my', { params }),
        '/training/assignments/my'
      ),
      
      // Get current user's assignment stats
      getMyStats: () => apiCall(
        () => apiClient.get('/training/assignments/stats'),
        '/training/assignments/stats'
      ),
      
      // Create assignments (assign training to users) - supports recurring
      create: (data) => apiCall(
        () => apiClient.post('/training/assignments', data),
        '/training/assignments'
      ),
      
      // Get single assignment details
      getById: (id) => apiCall(
        () => apiClient.get(`/training/assignments/${id}`),
        `/training/assignments/${id}`
      ),
      
      // Update progress (mark content viewed)
      updateProgress: (id, data) => apiCall(
        () => apiClient.put(`/training/assignments/${id}/progress`, data),
        `/training/assignments/${id}/progress`
      ),
      
      // Update recurrence settings for an assignment
      updateRecurrence: (id, data) => apiCall(
        () => apiClient.put(`/training/assignments/${id}/recurrence`, data),
        `/training/assignments/${id}/recurrence`
      ),

      // Waive (cancel) an assignment
      waive: (id, data) => apiCall(
        () => apiClient.put(`/training/assignments/${id}/waive`, data),
        `/training/assignments/${id}/waive`
      )
    },
    
    // ============= QUIZ =============
    quiz: {
      // Start quiz attempt
      start: (assignmentId) => apiCall(
        () => apiClient.post(`/training/assignments/${assignmentId}/quiz/start`),
        `/training/assignments/${assignmentId}/quiz/start`
      ),
      
      // Submit quiz answer
      submitAnswer: (assignmentId, data) => apiCall(
        () => apiClient.post(`/training/assignments/${assignmentId}/quiz/submit`, data),
        `/training/assignments/${assignmentId}/quiz/submit`
      ),
      
      // Complete quiz attempt
      complete: (assignmentId, attemptId) => apiCall(
        () => apiClient.post(`/training/assignments/${assignmentId}/quiz/complete`, { attemptId }),
        `/training/assignments/${assignmentId}/quiz/complete`
      ),

      // Review a pending explanation/practical response (manager only)
      review: (assignmentId, data) => apiCall(
        () => apiClient.put(`/training/assignments/${assignmentId}/quiz/review`, data),
        `/training/assignments/${assignmentId}/quiz/review`
      )
    },
    
    // ============= REPORTS =============
    reports: {
      // Get overview statistics
      getOverview: () => apiCall(
        () => apiClient.get('/training/reports/overview'),
        '/training/reports/overview'
      ),
      
      // Get stats for specific training
      getByTraining: (id) => apiCall(
        () => apiClient.get(`/training/reports/by-training/${id}`),
        `/training/reports/by-training/${id}`
      ),
      
      // Get users the manager can view training history for
      getViewableUsers: () => apiCall(
        () => apiClient.get('/training/reports/viewable-users'),
        '/training/reports/viewable-users'
      ),
      
      // Get viewable users assigned to a specific training (for filtering)
      getUsersByTraining: (trainingId) => apiCall(
        () => apiClient.get(`/training/reports/users-by-training/${trainingId}`),
        `/training/reports/users-by-training/${trainingId}`
      ),
      
      // Get aggregated team compliance summary (server-calculated)
      getTeamSummary: () => apiCall(
        () => apiClient.get('/training/reports/team-summary'),
        '/training/reports/team-summary'
      ),
      
      // Get real-time training stats for all trainings (for Training Overview table)
      getTrainingStats: () => apiCall(
        () => apiClient.get('/training/reports/training-stats'),
        '/training/reports/training-stats'
      ),
      
      // Get complete training history for a user
      getUserHistory: (userId) => apiCall(
        () => apiClient.get(`/training/reports/user/${userId}/history`),
        `/training/reports/user/${userId}/history`
      ),
      
      // Get all quiz attempts for a user (paginated)
      getUserAttempts: (userId, params = {}) => apiCall(
        () => apiClient.get(`/training/reports/user/${userId}/attempts`, { params }),
        `/training/reports/user/${userId}/attempts`
      ),
      
      // Get detailed quiz attempt with question-level responses (for managers)
      getAttemptDetails: (userId, attemptId) => apiCall(
        () => apiClient.get(`/training/reports/user/${userId}/attempt/${attemptId}/details`),
        `/training/reports/user/${userId}/attempt/${attemptId}/details`
      )
    },
    
    // ============= SCHEDULER (Admin) =============
    scheduler: {
      // Get scheduler status
      getStatus: () => apiCall(
        () => apiClient.get('/training/scheduler/status'),
        '/training/scheduler/status'
      ),
      
      // Manually trigger recurrence check
      triggerRecurrenceCheck: () => apiCall(
        () => apiClient.post('/training/scheduler/trigger-recurrence-check'),
        '/training/scheduler/trigger-recurrence-check'
      ),
      
      // Get assignments pending recurrence reset
      getPendingResets: () => apiCall(
        () => apiClient.get('/training/scheduler/pending-resets'),
        '/training/scheduler/pending-resets'
      )
    },

    // ============= ROLE TRAINING CONFIGS =============
    roleConfigs: {
      getAll: (params = {}) => apiCall(
        () => apiClient.get('/training/role-configs', { params }),
        '/training/role-configs'
      ),
      getById: (id) => apiCall(
        () => apiClient.get(`/training/role-configs/${id}`),
        `/training/role-configs/${id}`
      ),
      create: (data) => apiCall(
        () => apiClient.post('/training/role-configs', data),
        '/training/role-configs'
      ),
      update: (id, data) => apiCall(
        () => apiClient.put(`/training/role-configs/${id}`, data),
        `/training/role-configs/${id}`
      ),
      delete: (id) => apiCall(
        () => apiClient.delete(`/training/role-configs/${id}`),
        `/training/role-configs/${id}`
      )
    },

    // ============= ONBOARDING TEMPLATES =============
    onboardingTemplates: {
      getAll: (params = {}) => apiCall(
        () => apiClient.get('/training/onboarding-templates', { params }),
        '/training/onboarding-templates'
      ),
      getById: (id) => apiCall(
        () => apiClient.get(`/training/onboarding-templates/${id}`),
        `/training/onboarding-templates/${id}`
      ),
      create: (data) => apiCall(
        () => apiClient.post('/training/onboarding-templates', data),
        '/training/onboarding-templates'
      ),
      update: (id, data) => apiCall(
        () => apiClient.put(`/training/onboarding-templates/${id}`, data),
        `/training/onboarding-templates/${id}`
      ),
      delete: (id) => apiCall(
        () => apiClient.delete(`/training/onboarding-templates/${id}`),
        `/training/onboarding-templates/${id}`
      )
    }
  },

  // ============= USER ACTIVITY TRACKING =============
  activity: {
    // Send heartbeat (called every 5 seconds while app is active)
    heartbeat: () => apiCall(
      () => apiClient.post('/activity/heartbeat'),
      '/activity/heartbeat'
    ),
    
    // End session (called on logout or app going to background)
    endSession: () => apiCall(
      () => apiClient.post('/activity/end-session'),
      '/activity/end-session'
    ),
    
    // Get current user's activity stats
    getMyStats: (params = {}) => apiCall(
      () => apiClient.get('/activity/my-stats', { params }),
      '/activity/my-stats'
    ),
    
    // Admin endpoints (MANAGE_USERS only)
    admin: {
      // Get overall activity summary for all users
      getSummary: (params = {}) => apiCall(
        () => apiClient.get('/activity/admin/summary', { params }),
        '/activity/admin/summary'
      ),
      
      // Get detailed activity for a specific user
      getUserActivity: (userId, params = {}) => apiCall(
        () => apiClient.get(`/activity/admin/user/${userId}`, { params }),
        `/activity/admin/user/${userId}`
      ),
      
      // Get list of inactive users
      getInactiveUsers: (days = 7) => apiCall(
        () => apiClient.get('/activity/admin/inactive', { params: { days } }),
        '/activity/admin/inactive'
      ),
      
      // Manually trigger aggregation (for testing)
      triggerAggregation: (dateString) => apiCall(
        () => apiClient.post('/activity/admin/aggregate', { dateString }),
        '/activity/admin/aggregate'
      )
    }
  },

  // ============= PLC MONITORING =============
  plc: {
    // ============= GATEWAYS =============
    gateways: {
      // Get all gateways with signal counts
      getAll: () => apiCall(
        () => apiClient.get('/plc/gateways'),
        '/plc/gateways'
      ),
      
      // Get gateways for dropdown (lightweight)
      getForDropdown: () => apiCall(
        () => apiClient.get('/plc/gateways/for-dropdown'),
        '/plc/gateways/for-dropdown'
      ),
      
      // Get gateway statistics
      getStats: () => apiCall(
        () => apiClient.get('/plc/gateways/stats'),
        '/plc/gateways/stats'
      ),
      
      // Get single gateway by ID
      getById: (gatewayId) => apiCall(
        () => apiClient.get(`/plc/gateways/${gatewayId}`),
        `/plc/gateways/${gatewayId}`
      ),
      
      // Create new gateway
      create: (data) => apiCall(
        () => apiClient.post('/plc/gateways', data),
        '/plc/gateways'
      ),
      
      // Update gateway
      update: (gatewayId, data) => apiCall(
        () => apiClient.put(`/plc/gateways/${gatewayId}`, data),
        `/plc/gateways/${gatewayId}`
      ),
      
      // Delete gateway
      delete: (gatewayId, deleteSignals = false) => apiCall(
        () => apiClient.delete(`/plc/gateways/${gatewayId}`, {
          params: deleteSignals ? { deleteSignals: 'true' } : {}
        }),
        `/plc/gateways/${gatewayId}`
      ),
      
      // Generate authentication token for gateway service
      generateToken: (gatewayId) => apiCall(
        () => apiClient.post(`/plc/gateways/${gatewayId}/generate-token`),
        `/plc/gateways/${gatewayId}/generate-token`
      )
    },
    
    // ============= SIGNALS =============
    signals: {
      // Get all signals with optional filtering
      getAll: (params = {}) => apiCall(
        () => apiClient.get('/plc/signals', { params }),
        '/plc/signals'
      ),
      
      // Get signal groups
      getGroups: () => apiCall(
        () => apiClient.get('/plc/signals/groups'),
        '/plc/signals/groups'
      ),
      
      // Get single signal by ID
      getById: (signalId) => apiCall(
        () => apiClient.get(`/plc/signals/${signalId}`),
        `/plc/signals/${signalId}`
      ),
      
      // Create new signal
      create: (data) => apiCall(
        () => apiClient.post('/plc/signals', data),
        '/plc/signals'
      ),
      
      // Update signal
      update: (signalId, data) => apiCall(
        () => apiClient.put(`/plc/signals/${signalId}`, data),
        `/plc/signals/${signalId}`
      ),
      
      // Assign machines to signal
      assignMachines: (signalId, machineIds) => apiCall(
        () => apiClient.put(`/plc/signals/${signalId}/machines`, { machineIds }),
        `/plc/signals/${signalId}/machines`
      ),
      
      // Delete signal
      delete: (signalId) => apiCall(
        () => apiClient.delete(`/plc/signals/${signalId}`),
        `/plc/signals/${signalId}`
      ),
      
      // Get signals for a specific machine
      getByMachine: (machineId) => apiCall(
        () => apiClient.get(`/plc/machines/${machineId}/signals`),
        `/plc/machines/${machineId}/signals`
      )
    },
    
    // ============= SIGNAL HISTORY =============
    history: {
      // Get signal history with time range filtering
      get: (signalId, params = {}) => apiCall(
        () => apiClient.get(`/plc/signals/${signalId}/history`, { params }),
        `/plc/signals/${signalId}/history`
      ),
      
      // Get signal statistics
      getStats: (signalId, from, to) => apiCall(
        () => apiClient.get(`/plc/signals/${signalId}/history/stats`, {
          params: { from, to }
        }),
        `/plc/signals/${signalId}/history/stats`
      ),
      
      // Get chart data (downsampled for visualization)
      getChartData: (signalId, from, to, buckets = 100) => apiCall(
        () => apiClient.get(`/plc/signals/${signalId}/history/chart`, {
          params: { from, to, buckets }
        }),
        `/plc/signals/${signalId}/history/chart`
      )
    },
    
    // ============= STORAGE =============
    storage: {
      // Get storage statistics
      getStats: () => apiCall(
        () => apiClient.get('/plc/storage/stats'),
        '/plc/storage/stats'
      )
    }
  },

  // ============= ASSET TYPES =============
  assetTypes: {
    getAll: (params = {}) => apiCall(
      () => apiClient.get('/asset-types', { params }),
      '/asset-types'
    ),
    getById: (id) => apiCall(
      () => apiClient.get(`/asset-types/${id}`),
      `/asset-types/${id}`
    ),
    create: (data) => apiCall(
      () => apiClient.post('/asset-types', data),
      '/asset-types'
    ),
    update: (id, data) => apiCall(
      () => apiClient.put(`/asset-types/${id}`, data),
      `/asset-types/${id}`
    ),
    delete: (id) => apiCall(
      () => apiClient.delete(`/asset-types/${id}`),
      `/asset-types/${id}`
    ),
    seed: () => apiCall(
      () => apiClient.post('/asset-types/seed'),
      '/asset-types/seed'
    )
  },

  // ============= ASSET DOCUMENTS =============
  assetDocuments: {
    list: (assetId) => apiCall(
      () => apiClient.get(`/asset-documents/assets/${assetId}/documents`),
      `/asset-documents/assets/${assetId}/documents`
    ),
    upload: (assetId, formData) => apiCall(
      () => apiClient.post(
        `/asset-documents/assets/${assetId}/documents`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      ),
      `/asset-documents/assets/${assetId}/documents`
    ),
    get: (docId) => apiCall(
      () => apiClient.get(`/asset-documents/documents/${docId}`),
      `/asset-documents/documents/${docId}`
    ),
    delete: (docId) => apiCall(
      () => apiClient.delete(`/asset-documents/documents/${docId}`),
      `/asset-documents/documents/${docId}`
    ),
    reprocess: (docId) => apiCall(
      () => apiClient.post(`/asset-documents/documents/${docId}/reprocess`),
      `/asset-documents/documents/${docId}/reprocess`
    ),
    getStatus: (docId) => apiCall(
      () => apiClient.get(`/asset-documents/documents/${docId}/status`),
      `/asset-documents/documents/${docId}/status`
    ),
    reprocessAll: () => apiCall(
      () => apiClient.post(`/asset-documents/documents/reprocess-all`),
      `/asset-documents/documents/reprocess-all`
    ),
    /** Returns a URL string for the page image (not an API call) */
    getPageImageUrl: (docId, pageNumber) =>
      `${apiClient.defaults.baseURL}/asset-documents/documents/${docId}/page/${pageNumber}`,
    /** Link an existing document to additional assets */
    linkToAssets: (docId, assetIds) => apiCall(
      () => apiClient.post(`/asset-documents/documents/${docId}/link`, { assetIds }),
      `/asset-documents/documents/${docId}/link`
    ),
    /** Unlink a document from a specific asset */
    unlinkFromAsset: (docId, assetId) => apiCall(
      () => apiClient.delete(`/asset-documents/documents/${docId}/link/${assetId}`),
      `/asset-documents/documents/${docId}/link/${assetId}`
    ),
    /** Search documents by title/description (for LinkDocumentModal) */
    searchDocuments: (params = {}) => apiCall(
      () => apiClient.get('/asset-documents/documents/search', { params }),
      '/asset-documents/documents/search'
    ),
    /** List all documents with optional filtering and pagination */
    listAll: (params = {}) => apiCall(
      () => apiClient.get('/asset-documents/documents/all', { params }),
      '/asset-documents/documents/all'
    )
  },

  // ============= DOCUMENT CONTROL =============
  documents: {
    listAll: (params = {}) => apiCall(
      () => apiClient.get('/documents/documents/all', { params }),
      '/documents/documents/all'
    ),
    listByAsset: (assetId) => apiCall(
      () => apiClient.get(`/documents/assets/${assetId}/documents`),
      `/documents/assets/${assetId}/documents`
    ),
    uploadForAsset: (assetId, formData) => apiCall(
      () => apiClient.post(
        `/documents/assets/${assetId}/documents`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      ),
      `/documents/assets/${assetId}/documents`
    ),
    uploadStandalone: (formData) => apiCall(
      () => apiClient.post('/documents', formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }),
      '/documents'
    ),
    get: (docId) => apiCall(
      () => apiClient.get(`/documents/documents/${docId}`),
      `/documents/documents/${docId}`
    ),
    update: (docId, data) => apiCall(
      () => apiClient.patch(`/documents/documents/${docId}`, data),
      `/documents/documents/${docId}`
    ),
    delete: (docId) => apiCall(
      () => apiClient.delete(`/documents/documents/${docId}`),
      `/documents/documents/${docId}`
    ),
    reprocess: (docId) => apiCall(
      () => apiClient.post(`/documents/documents/${docId}/reprocess`),
      `/documents/documents/${docId}/reprocess`
    ),
    getStatus: (docId) => apiCall(
      () => apiClient.get(`/documents/documents/${docId}/status`),
      `/documents/documents/${docId}/status`
    ),
    reprocessAll: () => apiCall(
      () => apiClient.post('/documents/documents/reprocess-all'),
      '/documents/documents/reprocess-all'
    ),
    getPageImageUrl: (docId, pageNumber) =>
      `${apiClient.defaults.baseURL}/documents/documents/${docId}/page/${pageNumber}`,
    linkToAssets: (docId, assetIds) => apiCall(
      () => apiClient.post(`/documents/documents/${docId}/link`, { assetIds }),
      `/documents/documents/${docId}/link`
    ),
    unlinkFromAsset: (docId, assetId) => apiCall(
      () => apiClient.delete(`/documents/documents/${docId}/link/${assetId}`),
      `/documents/documents/${docId}/link/${assetId}`
    ),
    searchDocuments: (params = {}) => apiCall(
      () => apiClient.get('/documents/documents/search', { params }),
      '/documents/documents/search'
    ),
    uploadRevision: (docId, formData) => apiCall(
      () => apiClient.post(
        `/documents/documents/${docId}/revision`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      ),
      `/documents/documents/${docId}/revision`
    ),
    getVersions: (docId) => apiCall(
      () => apiClient.get(`/documents/documents/${docId}/versions`),
      `/documents/documents/${docId}/versions`
    ),
    getOverdue: () => apiCall(
      () => apiClient.get('/documents/documents/overdue'),
      '/documents/documents/overdue'
    ),

    // Authored document methods
    createAuthored: (data) => apiCall(
      () => apiClient.post('/documents/documents/authored', data),
      '/documents/documents/authored'
    ),
    updateAuthored: (docId, data) => apiCall(
      () => apiClient.patch(`/documents/documents/${docId}/authored`, data),
      `/documents/documents/${docId}/authored`
    ),
    uploadScreenshot: (docId, formData) => apiCall(
      () => apiClient.post(
        `/documents/documents/${docId}/screenshot`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      ),
      `/documents/documents/${docId}/screenshot`
    ),
    deleteScreenshot: (cloudinaryId) => apiCall(
      () => apiClient.delete(`/documents/documents/screenshot/${cloudinaryId}`),
      `/documents/documents/screenshot/${cloudinaryId}`
    ),
    getForMachine: (machineId, type = null) => apiCall(
      () => apiClient.get(`/documents/documents/machine/${machineId}`, { params: type ? { type } : {} }),
      `/documents/documents/machine/${machineId}`
    ),
    searchForMachine: (machineId, query, limit = 10) => apiCall(
      () => apiClient.get(`/documents/documents/machine/${machineId}/search`, { params: { q: query, limit } }),
      `/documents/documents/machine/${machineId}/search`
    )
  },

  // ============= ASSET TREE =============
  assetTree: {
    getTree: (params = {}) => apiCall(
      () => apiClient.get('/asset-tree/tree', { params }),
      '/asset-tree/tree'
    ),
    getAll: (params = {}) => apiCall(
      () => apiClient.get('/asset-tree', { params }),
      '/asset-tree'
    ),
    getStats: () => apiCall(
      () => apiClient.get('/asset-tree/stats'),
      '/asset-tree/stats'
    ),
    getById: (id) => apiCall(
      () => apiClient.get(`/asset-tree/${id}`),
      `/asset-tree/${id}`
    ),
    getChildren: (id) => apiCall(
      () => apiClient.get(`/asset-tree/${id}/children`),
      `/asset-tree/${id}/children`
    ),
    getDescendants: (id) => apiCall(
      () => apiClient.get(`/asset-tree/${id}/descendants`),
      `/asset-tree/${id}/descendants`
    ),
    create: (data) => apiCall(
      () => apiClient.post('/asset-tree', data),
      '/asset-tree'
    ),
    update: (id, data) => apiCall(
      () => apiClient.put(`/asset-tree/${id}`, data),
      `/asset-tree/${id}`
    ),
    delete: (id) => apiCall(
      () => apiClient.delete(`/asset-tree/${id}`),
      `/asset-tree/${id}`
    ),
    move: (id, data) => apiCall(
      () => apiClient.post(`/asset-tree/${id}/move`, data),
      `/asset-tree/${id}/move`
    ),
    linkAsset: (parentId, assetId) => apiCall(
      () => apiClient.post(`/asset-tree/${parentId}/link`, { assetId }),
      `/asset-tree/${parentId}/link`
    ),
    unlinkAsset: (parentId, assetId) => apiCall(
      () => apiClient.delete(`/asset-tree/${parentId}/link/${assetId}`),
      `/asset-tree/${parentId}/link/${assetId}`
    )
  }
};

// ============= UTILITY FUNCTIONS =============

// Manual logout (for error handling)
export const performLogout = () => {
  isRefreshing = false;
  processQueue(new Error('Logged out'), null);
  window.location.href = '/login';
};

// API health check
export const checkApiHealth = async () => {
  try {
    const response = await api.auth.health();
    return response.data;
  } catch (error) {
    return null;
  }
};



export default apiClient;

// Export API base URL for debugging
export { API_BASE_URL };